// Vendors page — LOCATION-FIRST: a vendor belongs to exactly one operation unit and can
// never be paid outside it (the same real-world vendor serving two locations has two
// separate entries), so an Operation-unit picker at the top scopes everything.
//
// The vendor endpoints read the OU from JWT claims: choosing a location mints a CONTEXT
// token (POST /api/account/selectLeOu with the normal auth token) which is held in PAGE
// state only and used as the bearer for the vendor list/save calls. The app's own auth
// token is never replaced.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Landmark, Plus, Search, ShieldOff } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useInitData } from "@/lib/init-data";
import {
  fetchVendors,
  mintVendorContextToken,
  type VendorBankAccountMap,
  type VendorMap,
} from "@/lib/vendors";
import { cn } from "@/lib/utils";
import { VendorFormSheet } from "@/components/vendor-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";

const PAGE_SIZE = 20;

export default function VendorsPage() {
  const { token } = useAuth();
  const initData = useInitData();

  // --- Location scope + context token (page state, never the app auth token) ---
  const [ouId, setOuId] = useState<number | null>(null);
  const [ctxToken, setCtxToken] = useState<string | null>(null);
  const [minting, setMinting] = useState(false);

  // --- List state ---
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // debounced, server-side
  const [rows, setRows] = useState<VendorMap[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noListAccess, setNoListAccess] = useState(false);

  // --- Detail sheet + form ---
  const [detail, setDetail] = useState<VendorMap | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editVendor, setEditVendor] = useState<VendorMap | null>(null);

  const canEdit = initData.canMakeRegularRequest;

  // Preselect the first (or only) operation unit once init data arrives.
  useEffect(() => {
    if (ouId == null && initData.myOperationUnits.length > 0) {
      setOuId(initData.myOperationUnits[0].id);
    }
  }, [ouId, initData.myOperationUnits]);

  // Mint a fresh context token whenever the chosen location changes.
  const mintSeq = useRef(0);
  useEffect(() => {
    setCtxToken(null);
    if (!token || ouId == null || !initData.loaded) return;
    const legalEntityId = initData.legalEntities[0]?.id;
    if (legalEntityId == null) {
      toast.error("No legal entity available for your account.");
      return;
    }
    const seq = ++mintSeq.current;
    setMinting(true);
    setNoListAccess(false);
    mintVendorContextToken(token, legalEntityId, ouId)
      .then((minted) => {
        if (seq !== mintSeq.current) return; // superseded by a newer location choice
        setCtxToken(minted);
      })
      .catch((e) => {
        if (seq !== mintSeq.current) return;
        if (e instanceof ApiError && e.status === 403) {
          toast.error("You don't have access to this location.");
        } else {
          toast.error(e instanceof ApiError ? e.message : "Could not switch location.");
        }
        setLoading(false);
      })
      .finally(() => {
        if (seq === mintSeq.current) setMinting(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, ouId, initData.loaded]);

  // Debounce the search box (300ms) — the filtering itself happens on the server.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const requestSeq = useRef(0);
  const load = useCallback(
    (pageToLoad: number, append: boolean) => {
      if (!ctxToken) return;
      const seq = ++requestSeq.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      fetchVendors(ctxToken, { search, currentPage: pageToLoad, pageSize: PAGE_SIZE })
        .then((d) => {
          if (seq !== requestSeq.current) return;
          setRows((prev) => (append ? [...prev, ...(d.list ?? [])] : d.list ?? []));
          setTotalCount(d.totalCount ?? 0);
          setPage(d.currentPage ?? pageToLoad);
          setNoListAccess(false);
        })
        .catch((e) => {
          if (seq !== requestSeq.current) return;
          if (e instanceof ApiError && e.status === 403) {
            setNoListAccess(true); // lacks the vendor-list grant
          } else {
            toast.error(e instanceof ApiError ? e.message : "Could not load vendors.");
          }
        })
        .finally(() => {
          if (seq !== requestSeq.current) return;
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [ctxToken, search]
  );

  // Reload from page 1 whenever the context token or (debounced) search changes.
  useEffect(() => {
    load(1, false);
  }, [load]);

  const ouOptions = useMemo(
    () => initData.myOperationUnits.map((u) => ({ id: u.id, name: u.name ?? String(u.id) })),
    [initData.myOperationUnits]
  );
  const bankOptions = useMemo(
    () => initData.banks.map((b) => ({ id: b.id, name: b.name ?? String(b.id) })),
    [initData.banks]
  );
  const ouName = useMemo(
    () => initData.myOperationUnits.find((u) => u.id === ouId)?.name ?? null,
    [initData.myOperationUnits, ouId]
  );

  const hasMore = rows.length < totalCount;
  const busyScope = minting || (!ctxToken && loading);

  if (noListAccess) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <span className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <ShieldOff className="size-7" strokeWidth={1.5} />
            </span>
            <div>
              <p className="text-sm font-semibold">You don't have permission to view vendors</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Ask your administrator for the vendor-list access.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* Location scope + Add */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-end justify-between gap-2">
          <Label className="flex-1">Operation unit</Label>
          {canEdit && (
            <Button
              size="sm"
              disabled={!ctxToken}
              onClick={() => {
                setEditVendor(null);
                setFormOpen(true);
              }}
            >
              <Plus /> Add vendor
            </Button>
          )}
        </div>
        <Combobox
          options={ouOptions}
          value={ouId}
          onValueChange={(id) => setOuId(id)}
          placeholder={ouOptions.length === 0 ? "No locations accessible" : "Select location…"}
          disabled={ouOptions.length === 0}
        />
        <p className="text-xs text-muted-foreground">
          Vendors can only be paid within their own location.
        </p>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search vendors…"
          className="pl-9"
          aria-label="Search vendors"
        />
      </div>

      {loading || busyScope ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-1 py-16 text-center">
          <p className="text-sm font-medium">No vendors</p>
          <p className="text-xs text-muted-foreground">
            {search
              ? "Try a different search."
              : "Vendors of this location will appear here."}
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {totalCount} vendor{totalCount === 1 ? "" : "s"}
            {ouName ? ` in ${ouName}` : ""}
          </p>
          <ul className="flex flex-col gap-2">
            {rows.map((v) => (
              <li key={v.id}>
                <VendorCard
                  vendor={v}
                  onOpen={() => {
                    setDetail(v);
                    setDetailOpen(true);
                  }}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {!loading && !busyScope && hasMore && (
        <Button
          variant="outline"
          className="w-full"
          disabled={loadingMore}
          onClick={() => load(page + 1, true)}
        >
          {loadingMore ? "Loading…" : `Load more (${rows.length} of ${totalCount})`}
        </Button>
      )}

      <VendorDetailSheet
        open={detailOpen}
        onOpenChange={setDetailOpen}
        vendor={detail}
        canEdit={canEdit}
        onEdit={() => {
          if (!detail) return;
          setDetailOpen(false);
          setEditVendor(detail);
          setFormOpen(true);
        }}
      />

      {token && (
        <VendorFormSheet
          open={formOpen}
          onOpenChange={setFormOpen}
          token={token}
          contextToken={ctxToken}
          vendor={editVendor}
          banks={bankOptions}
          operationUnitName={ouName}
          onSaved={() => {
            setDetail(null);
            load(1, false);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List card
// ---------------------------------------------------------------------------

function VendorCard({ vendor, onOpen }: { vendor: VendorMap; onOpen: () => void }) {
  const accounts = vendor.bankAccounts ?? [];
  const def = vendor.defaultBankAccount;
  return (
    <Card
      size="sm"
      className="cursor-pointer transition-colors active:bg-muted/50"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <CardContent className="flex flex-col gap-1.5">
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 text-sm font-medium">{vendor.name || "(unnamed vendor)"}</span>
          {vendor.verifiedForPayment && <PaymentVerifiedBadge />}
        </div>
        {(vendor.pan || vendor.panVerified) && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="font-mono">PAN {vendor.pan || "—"}</span>
            {vendor.panVerified && <PanVerifiedBadge />}
          </div>
        )}
        {(vendor.contactPerson || vendor.contactNumber) && (
          <div className="truncate text-xs text-muted-foreground">
            {vendor.contactPerson}
            {vendor.contactPerson && vendor.contactNumber ? " · " : ""}
            {vendor.contactNumber}
          </div>
        )}
        {vendor.email && <div className="truncate text-xs text-muted-foreground">{vendor.email}</div>}
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1.5">
            <Landmark className="size-3 shrink-0" />
            {def ? (
              <span className="truncate">
                {def.bank?.name || "Bank"} · {def.bankAccount || "—"}
              </span>
            ) : (
              <span>No bank account</span>
            )}
          </span>
          {accounts.length > 1 && (
            <span className="shrink-0">
              {accounts.length} account{accounts.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function PaymentVerifiedBadge() {
  return (
    <Badge
      variant="outline"
      className="shrink-0 border-green-300 bg-green-100 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-200"
    >
      Payment verified
    </Badge>
  );
}

function PanVerifiedBadge() {
  return (
    <Badge
      variant="outline"
      className="shrink-0 border-green-300 bg-green-100 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-200"
    >
      PAN verified
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Read-only detail sheet
// ---------------------------------------------------------------------------

function VendorDetailSheet({
  open,
  onOpenChange,
  vendor,
  canEdit,
  onEdit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendor: VendorMap | null;
  canEdit: boolean;
  onEdit: () => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85dvh] gap-0">
        <SheetHeader className="border-b border-border pr-12">
          <SheetTitle className="flex flex-wrap items-center gap-2">
            {vendor?.name || "Vendor"}
            {vendor?.verifiedForPayment && <PaymentVerifiedBadge />}
          </SheetTitle>
          {vendor?.operationUnit?.name && (
            <SheetDescription>
              {vendor.operationUnit.name} — payable only within this location.
            </SheetDescription>
          )}
        </SheetHeader>

        {vendor && (
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:shrink-0">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              <DetailRow label="PAN">
                <span className="flex items-center gap-1.5">
                  <span className="font-mono">{vendor.pan || "—"}</span>
                  {vendor.panVerified && <PanVerifiedBadge />}
                </span>
              </DetailRow>
              <DetailRow label="Address">{vendor.address || "—"}</DetailRow>
              <DetailRow label="Contact person">{vendor.contactPerson || "—"}</DetailRow>
              <DetailRow label="Contact number">{vendor.contactNumber || "—"}</DetailRow>
              <DetailRow label="Email">
                <span className="flex items-center gap-1.5">
                  <span className="min-w-0 break-all">{vendor.email || "—"}</span>
                  {vendor.email && vendor.emailVerified && <MiniVerified />}
                </span>
              </DetailRow>
              <DetailRow label="WhatsApp">
                <span className="flex items-center gap-1.5">
                  {vendor.whatsAppNumber || "—"}
                  {vendor.whatsAppNumber && vendor.whatsAppVerified && <MiniVerified />}
                </span>
              </DetailRow>
            </dl>

            <Separator />

            <h3 className="text-sm font-semibold">
              Bank accounts ({(vendor.bankAccounts ?? []).length})
            </h3>
            {(vendor.bankAccounts ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No bank accounts on record.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {(vendor.bankAccounts ?? []).map((a) => (
                  <li key={a.id}>
                    <BankAccountCard account={a} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {canEdit && vendor && (
          <SheetFooter className="border-t border-border">
            <Button onClick={onEdit}>Edit vendor</Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

function MiniVerified() {
  return (
    <Badge
      variant="outline"
      className="shrink-0 border-green-300 bg-green-100 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-200"
    >
      Verified
    </Badge>
  );
}

function BankAccountCard({ account }: { account: VendorBankAccountMap }) {
  return (
    <Card size="sm">
      <CardContent className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <span className="min-w-0 truncate text-sm font-medium">
            {account.bank?.name || "Bank"}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {account.isDefault && <Badge variant="secondary">Default</Badge>}
            <Badge
              variant="outline"
              className={cn(
                account.verifiedForPayment
                  ? "border-green-300 bg-green-100 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-200"
                  : "text-muted-foreground"
              )}
            >
              {account.verifiedForPayment ? "Verified" : "Unverified"}
            </Badge>
          </span>
        </div>
        <div className="font-mono text-sm">{account.bankAccount || "—"}</div>
        <div className="text-xs text-muted-foreground">
          IFSC {account.ifscCode || "—"}
          {account.bankBranch ? ` · ${account.bankBranch}` : ""}
        </div>
        {account.accountHolderName && (
          <div className="text-xs text-muted-foreground">A/c holder: {account.accountHolderName}</div>
        )}
      </CardContent>
    </Card>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Card size="sm" key={i}>
          <CardContent className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-5 w-24 rounded-full" />
            </div>
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
